import React, { useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import PageLayout from "../../components/PageLayout";
import Button from "../../components/Button";
import FormInput from "../../components/FormInput";
import Toast from "../../components/Toast";
import { uploadFile, FILE_UPLOAD_URL } from "../../utils/api";
import UploadIcon from "../../icons/upload.svg";
import "./VirtualGrading.css";

const UploadItem = () => {
  const history = useHistory();
  const fileInputRef = useRef(null);
  
  const [dragActive, setDragActive] = useState(false);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    make: "",
    model: "",
    year: "",
    partNumber: "",
    description: "",
  });
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = async (files) => {
    setUploading(true);
    const fileArray = Array.from(files);
    
    // For now, just create preview URLs
    // In production, you would upload to server here
    const newImages = fileArray.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      url: "", // Will be filled after upload
    }));
    
    setImages([...images, ...newImages]);
    setUploading(false);
    
    // TODO: Upload to server using uploadFile function
    // const uploadPromises = fileArray.map(file => uploadFile(FILE_UPLOAD_URL, file));
    // const results = await Promise.all(uploadPromises);
  };

  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }
    
    if (images.length === 0) {
      newErrors.images = "At least one image is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setToast({
        show: true,
        message: "Please fill in all required fields",
        type: "error",
      });
      return;
    }

    // TODO: Submit to API
    // For now, just show success and redirect
    setToast({
      show: true,
      message: "Item submitted successfully for grading!",
      type: "success",
    });
    
    setTimeout(() => {
      history.push("/virtual-grading");
    }, 2000);
  };

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <PageLayout 
      heading="Virtual Grading"
      icon={UploadIcon}
    >
      <div className="virtual-grading-container">
        <div className="vg-upload-form">
          {/* Image Upload Area */}
          <div
            className={`vg-dropzone ${dragActive ? "active" : ""} ${
              errors.images ? "error" : ""
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={handleButtonClick}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileInput}
              style={{ display: "none" }}
            />
            
            {images.length === 0 ? (
              <div className="vg-dropzone-content">
                <div className="vg-upload-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="64"
                    height="64"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                    <path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708l3-3z" />
                  </svg>
                </div>
                <h3 className="vg-upload-title">Upload your item</h3>
                <p className="vg-upload-subtitle">
                  Drag and drop your image here, or click to browse
                </p>
                <Button className="vg-choose-file-btn" onClick={handleButtonClick}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                    className="mr-8"
                  >
                    <path d="M4.406 1.342A5.53 5.53 0 0 1 8 0c2.69 0 4.923 2 5.166 4.579C14.758 4.804 16 6.137 16 7.773 16 9.569 14.502 11 12.687 11H10a.5.5 0 0 1 0-1h2.688C13.979 10 15 8.988 15 7.773c0-1.216-1.02-2.228-2.313-2.228h-.5v-.5C12.188 2.825 10.328 1 8 1a4.53 4.53 0 0 0-2.941 1.1c-.757.652-1.153 1.438-1.153 2.055v.448l-.445.049C2.064 4.805 1 5.952 1 7.318 1 8.785 2.23 10 3.781 10H6a.5.5 0 0 1 0 1H3.781C1.708 11 0 9.366 0 7.318c0-1.763 1.266-3.223 2.942-3.593.143-.863.698-1.723 1.464-2.383z" />
                    <path d="M7.646 4.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 5.707V14.5a.5.5 0 0 1-1 0V5.707L5.354 7.854a.5.5 0 1 1-.708-.708l3-3z" />
                  </svg>
                  Choose File
                </Button>
              </div>
            ) : (
              <div className="vg-image-preview-grid">
                {images.map((image, index) => (
                  <div key={index} className="vg-image-preview">
                    <img src={image.preview} alt={`Preview ${index + 1}`} />
                    <button
                      className="vg-image-remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeImage(index);
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <div className="vg-add-more" onClick={handleButtonClick}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
                  </svg>
                </div>
              </div>
            )}
          </div>
          {errors.images && <div className="vg-error-text">{errors.images}</div>}

          {/* Form Fields */}
          <div className="vg-form-fields">
            <div className="vg-form-group">
              <label className="vg-form-label">
                Title <span className="vg-required">*</span>
              </label>
              <input
                type="text"
                name="title"
                className="vg-form-input"
                placeholder="Enter a title for your item"
                value={formData.title}
                onChange={handleInputChange}
              />
              {errors.title && <div className="vg-error-text">{errors.title}</div>}
            </div>

            <div className="vg-form-group">
              <label className="vg-form-label">Make</label>
              <input
                type="text"
                name="make"
                className="vg-form-input"
                placeholder="e.g., Toyota, Ford"
                value={formData.make}
                onChange={handleInputChange}
              />
            </div>

            <div className="vg-form-group">
              <label className="vg-form-label">Model</label>
              <input
                type="text"
                name="model"
                className="vg-form-input"
                placeholder="e.g., Camry, F-150"
                value={formData.model}
                onChange={handleInputChange}
              />
            </div>

            <div className="vg-form-group">
              <label className="vg-form-label">Year</label>
              <input
                type="text"
                name="year"
                className="vg-form-input"
                placeholder="e.g., 2020"
                value={formData.year}
                onChange={handleInputChange}
              />
            </div>

            <div className="vg-form-group">
              <label className="vg-form-label">Part Number</label>
              <input
                type="text"
                name="partNumber"
                className="vg-form-input"
                placeholder="e.g., ABC123"
                value={formData.partNumber}
                onChange={handleInputChange}
              />
            </div>

            <div className="vg-form-group">
              <label className="vg-form-label">Description</label>
              <textarea
                name="description"
                className="vg-form-textarea"
                placeholder="Add any additional details about your item"
                rows="4"
                value={formData.description}
                onChange={handleInputChange}
              />
            </div>

            <Button
              className="vg-submit-button"
              onClick={handleSubmit}
              disabled={uploading}
            >
              {uploading ? "Uploading..." : "Submit for Grading"}
            </Button>
          </div>
        </div>
      </div>

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: "", type: "" })}
        />
      )}
    </PageLayout>
  );
};

export default UploadItem;

